import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';

/**
 * First-time password setup page.
 * Shown after a member's very first magic-link sign-in.
 * Once a password is set, all future visits use normal email + password login.
 */
export default function SetPassword() {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();

  const [form, setForm]     = useState({ password: '', confirm: '' });
  const [error, setError]   = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (form.password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (form.password !== form.confirm) {
      setError("Passwords don't match — double-check and try again.");
      return;
    }

    setLoading(true);
    try {
      await api.post('/auth/set-password', { password: form.password });
      // Update the in-memory user so the app knows a password is now set
      updateUser({ has_password: true });
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setError(err.response?.data?.error || 'Could not set password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const firstName = user?.name?.split(' ')[0] || 'there';

  return (
    <div className="flex items-center justify-center min-h-screen bg-reset-cream px-4">
      <div className="w-full max-w-md">

        {/* Header */}
        <div className="mb-8">
          <div className="text-3xl mb-3">👋</div>
          <h1 className="font-serif text-2xl font-bold text-reset-brown">Welcome, {firstName}!</h1>
          <p className="text-reset-terracotta text-sm mt-2">
            You're in. Set a password for your account so you can sign in easily next time.
          </p>
        </div>

        {/* Email display — so they know exactly what login they're creating */}
        <div className="mb-5 px-4 py-3 bg-white border border-gray-200 rounded-lg">
          <p className="text-xs text-gray-400 mb-0.5">You're creating a password for</p>
          <p className="text-sm font-semibold text-reset-brown">{user?.email}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-reset-brown mb-1">
              Create a password
            </label>
            <input
              type="password"
              name="password"
              value={form.password}
              onChange={handleChange}
              required
              autoComplete="new-password"
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck="false"
              placeholder="At least 8 characters"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-reset-brown mb-1">
              Confirm password
            </label>
            <input
              type="password"
              name="confirm"
              value={form.confirm}
              onChange={handleChange}
              required
              autoComplete="new-password"
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck="false"
              placeholder="Type it again"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
            />
          </div>

          {error && <p className="text-red-600 text-sm">{error}</p>}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-reset-terracotta text-white py-2.5 rounded-lg font-medium text-sm hover:opacity-90 disabled:opacity-50 transition"
          >
            {loading ? 'Setting up…' : 'Set my password & continue'}
          </button>
        </form>

        <p className="mt-5 text-xs text-gray-400 text-center">
          You can always change your password later in Settings.
        </p>
      </div>
    </div>
  );
}
